import React, { useState } from 'react';
import { View, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import '../../../styles/date-ranger-picker.css';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import ReportModal from './ReportModal';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { CamerasParamList } from '../../../typings/Params';
import { cameraService } from '../../../services/central-api/cameras';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import Filter from './Filters';

export default function Reports({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<CamerasParamList, 'CamerasReports'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<CamerasParamList['CamerasReports']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasReports', filters],
        queryFn: () => cameraService.getReports(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleFormClose() {
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function canDownloadReport(row: CameraOfflineReport) {
        return (
            authenticatedUser?.permissions.camera_offline_historic_report
        ) && row.status === 'success' && !isDownloading;
    }

    return (
        <Container>
            <Header
                title={translate('cameraReports')}
                actions={authenticatedUser?.permissions.camera_offline_historic_report &&
                    <CustomButton text={translate('register')} onPress={() => {
                        setModalVisible(true);
                    }} />
                }
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredCameraReports')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true
                            },
                            {
                                name: translate('user'),
                                selector: row => row.user?.name || '',
                                wrap: true
                            },
                            {
                                name: 'Token',
                                selector: row => row.token,
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                grow: 0,
                                cell: row =>
                                    <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center' }}>
                                        {row.status == 'pending' ?
                                            <ActivityIndicator size={16} color={theme.colors.loadingColor} /> :
                                            <FontAwesomeIcon
                                                style={styles.statusStyle(row.status)}
                                                fontSize={16}
                                                icon={row.status == 'success' ? faCheckCircle : faTimesCircle}
                                            />
                                        }
                                    </View>
                            },
                            {
                                name: translate('filters'),
                                cell: row => <View style={{ width: '100%' }}>
                                    <View style={{ flexDirection: 'column', rowGap: 5 }}>
                                        <MyAppText>{translate('format')}: {row.filters.isPdf ? 'PDF' : 'XLSX'}</MyAppText>
                                        {row.filters.startDate && <MyAppText>{translate('start')}: {moment(row.filters.startDate).format('DD/MM/YYYY')}</MyAppText>}
                                        {row.filters.endDate && <MyAppText>{translate('end')}: {moment(row.filters.endDate).format('DD/MM/YYYY')}</MyAppText>}
                                        {
                                            (authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports)
                                            && row.actingBody && <MyAppText>{translate('actingBody')}: {row.actingBody.name}</MyAppText>
                                        }
                                    </View>
                                </View>
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                                wrap: true
                            },
                            {
                                button: true,
                                cell: row =>
                                    canDownloadReport(row) ? <DropdownButton
                                        actions={[
                                            {
                                                content: translate('downloadReport'),
                                                onPress: async () => {
                                                    if (!canDownloadReport(row)) {
                                                        return;
                                                    }

                                                    try {
                                                        setIsDownloading(true);
                                                        const video = await cameraService.getReport(row.id);
                                                        window.open(video.resultUrl);
                                                    } catch (error) {
                                                        Toast.show({
                                                            type: 'sentinelxError',
                                                            text1: translate('unexpectedError'),
                                                        });
                                                    } finally {
                                                        setIsDownloading(false);
                                                    }
                                                }
                                            }
                                        ]}
                                    /> : null
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            <ReportModal
                isModalVisible={isModalVisible}
                onClose={handleFormClose}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
    tagMarker: {
        borderRadius: 8,
        height: 16,
        paddingLeft: 10,
        paddingRight: 10,
        marginRight: 8,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
}));
